
<template>
    <div id="app" class="h-fix">
        <component :is="layout" class="h-fix" />
        <detect-network @detected-condition="onConnectivityChange" />
        <nio-notifications placement="top-center" />
        <nio-notifications placement="top-right" />
        <send-payment-link-modal />
    </div>
</template>

<script>
    import {
        mapActions,
        mapState,
        mapGetters,
        mapMutations,
    } from 'vuex';
    import DetectNetwork from 'v-offline';
    import { shortnameToOutput } from '@/utils/emoji.js';

    import AuthLayout from '@/layouts/Auth.vue';
    import DefaultLayout from '@/layouts/Default.vue';
    import SendPaymentLinkModal from '@/components/Modals/SendPaymentLinkModal.vue';

    export default {
        components: {
            AuthLayout,
            DefaultLayout,
            DetectNetwork,
            SendPaymentLinkModal,
        },

        data() {
            return {
                connectionNotification: null,
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('chat', [
                'conversations',
                'selectedContactId',
                'userRelatedId',
            ]),

            ...mapState('campaign', [
                'selectedCampaign',
            ]),

            ...mapGetters([
                'partner',
            ]),

            ...mapGetters('chat', [
                'selectedConversation',
            ]),

            layout() {
                const layout = this.$route.meta.layout || 'default';

                return `${layout}-layout`;
            },

            isFetching() {
                return this.$wait.is('fetching.conversations');
            },
        },

        watch: {
            '$route.name'() {
                this.redirectToChat();

                // TODO: Do not refresh all the time.
                this.refreshUser();
            },

            '$i18n.locale': {
                immediate: true,
                handler(newValue) {
                    this.updateLocale(newValue);
                },
            },

            'authUser.locale': {
                immediate: true,
                handler(newValue) {
                    if (!newValue) {
                        return;
                    }

                    this.$i18n.locale = newValue;
                },
            },

            'authUser.timezone': {
                immediate: true,
                handler(newValue) {
                    if (!newValue) {
                        return;
                    }

                    moment.tz.setDefault(newValue);
                },
            },

            'authUser.account.id': {
                immediate: true,
                handler(newValue) {
                    if (!newValue) {
                        return;
                    }

                    this.resetFetch();

                    this.$nextTick(() => {
                        this.fetchTemplateCategories();
                        this.fetchAutomations();
                    });
                },
            },

            $mq() {
                this.redirectToChat();
            },

            isFetching(newValue) {
                if (!newValue) {
                    this.sendLeadSelected();
                }
            },
        },

        methods: {
            ...mapActions([
                'fetchUser',
                'setAuthUser',
                'setAuthUserAccount',
                'clearAuthUser',
            ]),

            ...mapActions('chat', [
                'fetchTemplateCategories',
                'getSelectedConversationFromRelated',
            ]),

            ...mapActions('automation', [
                'fetchAutomations',
            ]),

            ...mapMutations([
                'setPageTitle',
            ]),

            ...mapMutations('appointment', [
                'addAppointment',
                'updateAppointment',
            ]),

            ...mapMutations('chat', [
                'addMessage',
                'markAllMessagesAsRead',
                'resetFetch',
                'setContactRelatedId',
                'setSelectedContactId',
                'setShowActiveConversation',
                'setUserRelatedId',
                'updateConversationContact',
                'updateConversationContactPhone',
            ]),

            ...mapMutations('contact', [
                'updateContact',
                'updateContactPhone',
            ]),

            ...mapMutations('campaign', [
                'addPlaceholder',
            ]),

            ...mapMutations('payment', [
                'addPayment',
                'updatePayment',
            ]),

            async sendLeadSelected() {
                if (this.isFetching || !this.$configs.inIframe) {
                    return;
                }

                const conversation = await this.getSelectedConversationFromRelated({
                    selectedContactId: this.selectedContactId,
                    userRelatedId: this.userRelatedId,
                });

                this.setShowActiveConversation(conversation.active);

                this.$router.replace({
                    name: 'chat',
                    params: {
                        id: conversation.id,
                    },
                }).catch(() => {});

                this.$nextTick(() => {
                    parentIFrame.sendMessage('leadSelected');
                });
            },

            updateLocale(locale) {
                // Set locale for plugins
                moment.locale(locale);
                this.$axios.defaults.headers.common['X-Locale'] = locale;

                // Save in local storage
                localStorage.setItem('locale', locale);

                // Update page title
                this.setPageTitle(this.$route.meta.title ? this.$t(this.$route.meta.title) : '');
            },

            async newMessage(message) {
                this.addMessage({ message });

                const isCurrentConversation = this.$route.name === 'chat' && message.conversation_id == this.$route.params.id;

                if (isCurrentConversation) {
                    this.markAllMessagesAsRead(message.conversation_id);
                    this.$axios.post('messages/read', { conversation_id: message.conversation_id });
                } else {
                    this.showNewMessageNotification(message);
                }
            },

            showNewMessageNotification(message) {
                const isIncomingMessage = message.direction == 'I';
                const isNotCurrentConversation = this.$route.name != 'chat' || message.conversation_id != this.$route.params.id;
                const shouldShowNotification = !this.hasOption('notifications.message_received') || this.getOption('notifications.message_received.screen');
                const messageBody = message.attachments ? this.$t('chat.conversation.picture') : message.body;

                if (isIncomingMessage && isNotCurrentConversation && shouldShowNotification) {
                    this.$notify.show({
                        placement: 'top-right',
                        message: this.formatFullName(message.conversation.contact_phone.contact),
                        description: shortnameToOutput(messageBody),
                        icon: 'regular/messages-bubble',
                        link: {
                            name: 'chat',
                            params: {
                                id: message.conversation.id,
                            },
                        },
                    });
                }
            },

            refreshUser() {
                if (empty(this.$route.query.token) &&
                    !this.$configs.inIframe &&
                    !this.$route.meta.auth
                ) {
                    const authUser = JSON.parse(localStorage.getItem('authUser'));

                    if (empty(authUser)) {
                        this.clearAuthUser();
                        return;
                    }

                    // Fetch user to make sure it's up to date
                    this.fetchUser();
                }
            },

            onConnectivityChange(isConnected) {
                if (isConnected) {
                    if (this.connectionNotification) {
                        this.connectionNotification.hide();
                        this.connectionNotification = null;

                        this.$notify.success(this.$t('global.network.connectionRetrieved'));
                    }
                } else {
                    this.connectionNotification = this.$notify.warning(this.$t('global.network.unableToConnect'), {
                        timeout: 0,
                        closable: false,
                        description: this.$t('global.network.connectedToInternet'),
                        icon: '',
                    });
                }
            },

            listenToAccountBroadcasting() {
                if (!this.$broadcasting.channels.account) {
                    return;
                }

                this.$broadcasting.channels.account
                    .listen('Appointment.AppointmentUpdated', ({ appointment }) => {
                        this.updateAppointment(appointment);
                    })
                    .listen('Appointment.AppointmentCreated', ({ appointment }) => {
                        this.addAppointment(appointment);
                    })
                    .listen('Campaign.ContactLinked', ({ contact, campaign }) => {
                        this.$bus.emit('campaign.contactLinked', { contact, campaign });
                    })
                    .listen('Campaign.PlaceholderLinked', ({ placeholder, campaign }) => {
                        // Make sure the campaign is the selected
                        if (this.selectedCampaign.id == campaign.id) {
                            this.addPlaceholder(placeholder);
                        }
                    })
                    .listen('Campaign.CampaignMessageSent', ({ contactId }) => {
                        this.$bus.emit('campaign.messageSent', contactId);
                    })
                    .listen('Campaign.CampaignReplyReceived', ({ contactId, reply }) => {
                        this.$bus.emit('campaign.replyReceived', { contactId, reply });
                    })
                    .listen('Contact.ContactUpdated', ({ contact }) => {
                        this.updateContact(contact);
                        this.updateConversationContact(contact);
                    })
                    .listen('ContactPhone.ContactPhoneUpdated', ({ contactPhone }) => {
                        this.updateContactPhone(contactPhone);
                        this.updateConversationContactPhone(contactPhone);
                    })
                    .listen('Payment.PaymentCreated', ({ payment }) => {
                        this.addPayment(payment);
                    })
                    .listen('Payment.PaymentUpdated', ({ payment }) => {
                        this.updatePayment(payment);
                    });
            },

            listenToUserBroadcasting() {
                if (!this.$broadcasting.channels.user) {
                    return;
                }

                this.$broadcasting.channels.user
                    .notification((notification) => {
                        if (notification.type.includes('MessageReceived') ||
                            notification.type.includes('MessageSent')
                        ) {
                            this.newMessage(notification.message);
                        }
                    })
                    .listen('Message.NewDeliveryReceipt', ({ message }) => {
                        this.addMessage({ message });
                    })
                    .listen('Conversation.MessageRead', ({ conversationId }) => {
                        this.markAllMessagesAsRead(conversationId);
                    })
                    .listen('Payment.PaymentReceived', ({ payment, conversationId, phoneNumber }) => {
                        const number = payment.invoice_number;

                        this.$notify.show({
                            placement: 'top-right',
                            message: this.$t('global.notifications.paymentReceivedTitle'),
                            description: this.$tc('global.notifications.paymentReceivedBody', !number, {
                                number,
                                contact: this.formatFullName(payment.contact) || this.formatPhone(phoneNumber),
                            }),
                            icon: 'regular/accounting-document',
                            link: {
                                name: 'chat',
                                params: {
                                    id: conversationId,
                                },
                            },
                        });
                    });
            },

            redirectToChat() {
                const isNotInChat = !this.$route.meta.auth && this.$route.name != 'chat';
                const isMobile = ['xs', 'sm', 'md', 'lg'].includes(this.$mq);

                if ((isMobile || this.$configs.inIframe) && isNotInChat) {
                    this.$router.replace({ name: 'chat' });
                }
            },

            initWithIframe() {
                window.iFrameResizer = {
                    targetOrigin: '*',
                    readyCallback: () => {
                        parentIFrame.sendMessage('ready');
                    },
                    messageCallback: async (data) => {
                        if (!data.accessToken) {
                            return;
                        }

                        if (data.userId) {
                            this.setUserRelatedId(data.userId);
                        }

                        if (data.leadId) {
                            this.setContactRelatedId(data.leadId);
                        }

                        if (data.contactId) {
                            this.setSelectedContactId(data.contactId);
                        }

                        this.setAuthUser({ access_token: data.accessToken });

                        const response = await this.fetchUser();
                        const authUser = response.data.data;

                        if (data.accountId) {
                            const account = authUser.accounts.find(a => a.related_id == data.accountId);

                            if (account) {
                                this.setAuthUserAccount(account);
                            }
                        }

                        this.$router.push({ name: 'chat' });
                    },
                };
            },

            async initWithToken() {
                // Authenticate user if token in URL
                this.setAuthUser({
                    access_token: this.$route.query.token,
                });

                await this.fetchUser();

                this.$router.push({ name: 'chat' });
            },

            init() {
                const authUser = JSON.parse(localStorage.getItem('authUser'));

                if (authUser && empty(this.$route.query.token)) {
                    this.setAuthUser(authUser);
                }
            },
        },

        created() {
            this.listenToAccountBroadcasting();
            this.listenToUserBroadcasting();

            this.$broadcasting.$on('account-channel-ready', this.listenToAccountBroadcasting);
            this.$broadcasting.$on('user-channel-ready', this.listenToUserBroadcasting);

            if (this.$configs.inIframe) {
                this.initWithIframe();
            } else if (!empty(this.$route.query.token)) {
                this.initWithToken();
            } else {
                this.init();
            }
        },

        mounted() {
            this.refreshUser();
        },

        beforeDestroy() {
            this.$broadcasting.$off('account-channel-ready', this.listenToAccountBroadcasting);
            this.$broadcasting.$off('user-channel-ready', this.listenToUserBroadcasting);
        },
    };
</script>
